import { createContext, useContext } from "react";

/**
 * Slot the {@link AppLayoutShell} reserves for a root page's top bar
 * (breadcrumbs + global actions). The slot spans the full content column —
 * including over the AI chat rail — so a page that portals its bar here gets
 * one horizontal row across the entire layout, with the chat panel starting
 * below it.
 */
export interface RootPageTopBarSlot {
  /** Portal target. `null` until the shell has mounted the slot. */
  node: HTMLElement | null;
  /** Live measured height of the slot, 0 while empty. */
  height: number;
}

export const RootPageTopBarContext = createContext<RootPageTopBarSlot | null>(
  null
);

/** `null` outside an {@link AppLayoutShell} (specs, storybook). */
export const useRootPageTopBarSlot = () => useContext(RootPageTopBarContext);

/**
 * The height a root page fills: the viewport below the alert banner and the
 * shell's top-bar row. A root page pins the document — it owns its scroll
 * region — so this is the one height both the page and the chat rail beside
 * it take. Two separate calculations let the rail drift a banner's height
 * taller than the page, which scrolled the whole document and pushed the
 * chat's composer below the fold.
 */
export const rootPageViewportHeight = ({
  alertBannerHeight,
  topBarHeight,
}: {
  alertBannerHeight: number;
  topBarHeight: number;
}) => `calc(100dvh - ${alertBannerHeight + topBarHeight}px)`;
